import {
  OEButton,
  SelectDropdownInput,
  flex,
  ml,
  mt,
  zIndex,
} from '@openeducation/oe-rn-components';
import {
  FREE_FORM_LESSON_UUID,
  LiveClassLesson,
} from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import React, { useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import type SelectDropdown from 'react-native-select-dropdown';

import { LiveClassLessonDownloadButton } from '../../atoms/live-class-lesson-download-button/live-class-lesson-download-button';
import LessonEvaluateTooltip from '../lesson-evaluate-tooltip/lesson-evaluate-tooltip';

export interface LessonSelectorProps {
  lessonId?: number;
  isLessonSubmitted: boolean;
  lessonList: LiveClassLesson[];
  onLessonSelected: (lessonId: number) => void;
  onSubmitLesson: (lessonId: number) => void;
  isSubmitting: boolean;
  showTooltip?: boolean;
  isFreeFormTopic: boolean;
}

const LessonSelector = ({
  lessonId,
  isLessonSubmitted,
  lessonList,
  onSubmitLesson,
  onLessonSelected,
  showTooltip,
  isSubmitting,
  isFreeFormTopic,
}: LessonSelectorProps) => {
  const dropdownRef = useRef<SelectDropdown>(null);

  useEffect(() => {
    if (!lessonId) {
      dropdownRef.current?.reset();
    }
  }, [lessonId]);

  const items = useMemo(
    () =>
      lessonList.map(({ id, displayName, sequence, uuid }) => ({
        key: id,
        value: displayName,
        indicator:
          sequence > 0 && uuid !== FREE_FORM_LESSON_UUID ? sequence : null,
      })),
    [lessonList]
  );

  const selectedItem = useMemo(
    () => lessonList.find(({ id }) => id === lessonId),
    [lessonList, lessonId]
  );

  const handleSelectLesson = ({ key }: { key: number }) =>
    onLessonSelected(key);

  const handleSubmitLesson = () => {
    if (!lessonId) return;
    onSubmitLesson(lessonId);
  };

  const lessonSelected = useMemo(
    () => items.find(({ key }) => key === lessonId),
    [lessonId, items]
  );

  return (
    <View style={[flex('flex-row'), zIndex(1)]}>
      <SelectDropdownInput
        ref={dropdownRef}
        defaultValue={lessonSelected}
        defaultButtonText="Select lesson"
        onSelect={handleSelectLesson}
        data={items}
      />
      {isLessonSubmitted ? (
        selectedItem?.lessonMaterialUrl &&
        !isFreeFormTopic && (
          <View
            style={[
              ml(24),
              showTooltip
                ? null
                : flex('justify-content-center', 'align-items-center'),
            ]}
          >
            <LiveClassLessonDownloadButton
              url={selectedItem?.lessonMaterialUrl}
            />

            {showTooltip && (
              <LessonEvaluateTooltip style={[mt(8), flex('align-items-end')]} />
            )}
          </View>
        )
      ) : (
        <View style={[ml(10), flex('justify-content-center')]}>
          <SubmitButton
            type="accent"
            disabled={!lessonSelected || !lessonId || isSubmitting}
            loading={isSubmitting}
            onPress={handleSubmitLesson}
          >
            Confirm Class
          </SubmitButton>
        </View>
      )}
    </View>
  );
};

const SubmitButton = styled(OEButton)`
  text-align: center;
  justify-content: center;
  height: 40px;
  width: 187px;
`;

export default LessonSelector;
